import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Checkbox from '@rsos/components/capstone/base/Checkbox';
import { ESRI } from '@rsos/constants/mapProviders';
import {
  GISStateSublayer,
  gisSublayerFor,
  updateSublayer,
} from '@rsos/reduxDataStore';
import { selectMapProvider } from '@rsos/sinatra';

const CompactCheckbox = styled(Checkbox)`
  padding: 1px 0;
`;

interface CompanionRoleControlProps {
  layerId: string;
  /** Stable role label shown in Map Layers (Footprint / Image). */
  label: string;
  /** All leaf sublayers for this role under the WFS group. */
  sublayers: GISStateSublayer[];
}

/**
 * One checkbox for a companion role (Footprint or Image) that toggles every
 * matching leaf under the WFS GroupLayer — so multiple feature types / WMS
 * names share a single control.
 */
export const CompanionRoleControl = memo<CompanionRoleControlProps>(
  ({ layerId, label, sublayers }) => {
    const isDisabled = useSelector(selectMapProvider) !== ESRI;
    const dispatch = useDispatch();

    const isChecked = useMemo(
      () => sublayers.some(sl => sl.active),
      [sublayers],
    );

    const toggle = useCallback(() => {
      const newVisible = !isChecked;

      sublayers.forEach(sl => {
        const gisSublayer = gisSublayerFor(sl);
        if (gisSublayer) {
          gisSublayer.visible = newVisible;
        }
        dispatch(
          updateSublayer({
            layerID: layerId,
            sublayerID: sl.uniqueId,
            active: newVisible,
          }),
        );
      });
    }, [dispatch, isChecked, layerId, sublayers]);

    if (sublayers.length === 0) {
      return null;
    }

    return (
      <CompactCheckbox
        label={label}
        isChecked={isChecked}
        disabled={isDisabled}
        onChange={toggle}
      />
    );
  },
);
